// frontend/packages/business-console/src/pages/Pulse/usePulseData.js
//
// Single hook the Pulse page uses to load section data. Two modes:
//
//   - `?demo=1` query string OR `localStorage["pulse.demo"] === "1"`:
//     returns the handcrafted fixtures from `demoData.js`. Used for
//     marketing screenshots without touching real tenant data.
//
//   - Default: fans out to /api/v2/console/pulse/{money,pipeline,crm}
//     in parallel. The three sections backed by real data are wired
//     today; inbox / visibility / tasks / performance / connect stay
//     on their empty-state copy until their backends land.
//
// Per-section failures don't kill the page — a fetch that throws
// leaves that section's `data.<section>` at `null` (which the section
// components already render as the empty state). The overall hook
// only reports `error` if EVERY wired fetch failed, which would mean
// the tenant has lost connectivity to the backend entirely.

import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { demoPulse } from './demoData';
import {
  fetchCrmPulse,
  fetchMoneyPulse,
  fetchPipelinePulse,
  fetchTasksPulse,
  fetchVisibilityPulse,
} from '../../services/pulseService';
import { getVisitorsSummary } from '../../services/visitorsService';

const EMPTY_PULSE = Object.freeze({
  money: null,
  pipeline: null,
  crm: null,
  inbox: null,
  visibility: null,
  tasks: null,
  performance: null,
  connect: null,
  // Site analytics — fed by /api/v2/visitors/summary. Null until the
  // backend has rolled up at least yesterday OR a live event today.
  visitors: null,
});

function readDemoFlag(searchString) {
  // Query string wins; localStorage is a sticky alternative for the
  // marketing person taking screenshots so they don't have to
  // re-add `?demo=1` to every URL.
  const params = new URLSearchParams(searchString || '');
  if (params.get('demo') === '1') return true;
  try {
    return localStorage.getItem('pulse.demo') === '1';
  } catch {
    return false;
  }
}

async function settled(promise) {
  try {
    return { ok: true, value: await promise };
  } catch (err) {
    return { ok: false, error: err };
  }
}

export function usePulseData() {
  const location = useLocation();
  const isDemo = useMemo(
    () => readDemoFlag(location.search),
    [location.search]
  );

  const [data, setData] = useState(isDemo ? demoPulse : EMPTY_PULSE);
  const [loading, setLoading] = useState(!isDemo);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    if (isDemo) {
      setData(demoPulse);
      setLoading(false);
      setError(null);
      return () => {
        cancelled = true;
      };
    }

    setLoading(true);
    setError(null);

    (async () => {
      const [money, pipeline, crm, tasks, visibility, visitors] =
        await Promise.all([
          settled(fetchMoneyPulse()),
          settled(fetchPipelinePulse()),
          settled(fetchCrmPulse()),
          settled(fetchTasksPulse()),
          settled(fetchVisibilityPulse()),
          // Site analytics — non-blocking; an outage on this fetch must
          // never hide the rest of Pulse.
          settled(getVisitorsSummary()),
        ]);

      if (cancelled) return;

      setData({
        ...EMPTY_PULSE,
        money: money.ok ? money.value : null,
        pipeline: pipeline.ok ? pipeline.value : null,
        crm: crm.ok ? crm.value : null,
        tasks: tasks.ok ? tasks.value : null,
        // visibility may legitimately be null (no scan yet) — the section
        // renders its own empty state in that case.
        visibility: visibility.ok ? visibility.value : null,
        // visitors is null until the backend has produced any rollup —
        // VisitorsSection handles the empty state.
        visitors: visitors.ok ? visitors.value : null,
      });

      // Surface an error only if every wired section failed — the page
      // is otherwise useful (each section renders its empty state on a
      // missing piece). visitors is intentionally excluded from the
      // "everything failed" check because it's a new, low-priority
      // surface; a visitors-fetch error shouldn't trigger the global
      // error banner.
      const allFailed = !money.ok && !pipeline.ok && !crm.ok && !tasks.ok;
      setError(
        allFailed
          ? money.error || pipeline.error || crm.error || tasks.error
          : null
      );
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [isDemo]);

  return { data, loading, error, isDemo };
}
